import { PluginObject } from 'vue'

import KY, { Options, Hooks } from 'ky'
import { API } from './types'
import { authApi } from './auth'
import { userApi } from './user'
import { scheduleApi } from './schedule'
import { scheduleTypeApi } from './scheduleType'
import { scheduleCommentApi } from './scheduleComment'
import { callApi } from './call'
import { teamApi } from './team'
import { messageApi } from './message'
import { dispensationApi } from './dispensation'
import { labotestApi } from './labotest'
import { medicineApi } from './medicine'
import { imageApi } from './image'
import { documentApi } from './document'
import { prescriptionApi } from './prescription'
import { dicomApi } from './dicom'
import { vitalsignApi } from './vitalsign'
import { medicationInstructionApi } from './medicationInstruction'
import { pdApi } from './pd'
import { pdSettingApi } from './pdSetting'
import { glucoseApi } from './glucose'
import { invitationApi } from './invitation'
import { hdApi } from './hd'
import { ecgApi } from './ecg'
import { roundApi } from './round'
import { alertApi } from './alert'
import store from '@/store'
import router from '@/router'
import { getEnvVar } from '@/utils'

const API_ENDPOINT = getEnvVar('API_ENDPOINT') as string

// eslint-disable-next-line
const _deepAssign = (target: any, assign: any) => {
  Object.keys(assign).forEach(key => {
    if (typeof assign[key] != 'object' || assign[key] === null) {
      target[key] = assign[key]
    } else if (Array.isArray(assign[key])) {
      target[key] = [].slice.apply(assign[key])
    } else {
      if (typeof target[key] != 'object' || target[key] === null) {
        target[key] = {}
      }
      _deepAssign(target[key], assign[key])
    }
  })
  return target
}

// eslint-disable-next-line
const deepAssign = (target: any, ...args: any[]) => {
  args.forEach(v => {
    _deepAssign(target, v)
  })
  return target
}

const _api = (): API => {
  const timeout = false
  const headers = {
    'content-type': 'application/json'
    // eslint-disable-next-line
  } as any

  const hooks: Hooks = {
    afterResponse: [
      async (request, options, response) => {
        if (response.status === 401) {
          try {
            await store.dispatch('user/refreshToken')

            if (!store.getters['user/isLogin']) {
              throw new Error('Refreshing a token failed')
            }

            return KY(request.url, deepAssign(options, { headers }))
          } catch (e) {
            const loginPathname = /\/w\//.test(location.pathname)
              ? 'Login'
              : 'PatientLogin'
            router.push({ name: loginPathname })
            return
          }
        }

        return response
      }
    ]
  }

  const ky = (url: string, options?: Options) =>
    KY(
      API_ENDPOINT + url,
      deepAssign({}, { headers, hooks, timeout }, options || {})
    )

  return {
    setHeader: (key, value) => {
      headers[key] = value
    },
    removeHeader: key => {
      delete headers[key]
    },
    ky,
    // 他のAPIモジュールはこちらに列挙してください。
    auth: authApi(ky),
    user: userApi(ky),
    schedule: scheduleApi(ky),
    call: callApi(ky),
    team: teamApi(ky),
    scheduleType: scheduleTypeApi(ky),
    scheduleComment: scheduleCommentApi(ky),
    message: messageApi(ky),
    dispensation: dispensationApi(ky),
    medicine: medicineApi(ky),
    document: documentApi(ky),
    labotest: labotestApi(ky),
    prescription: prescriptionApi(ky),
    image: imageApi(ky),
    dicom: dicomApi(ky),
    vitalsign: vitalsignApi(ky),
    medicationInstruction: medicationInstructionApi(ky),
    pd: pdApi(ky),
    pdSetting: pdSettingApi(ky),
    glucose: glucoseApi(ky),
    invitation: invitationApi(ky),
    hd: hdApi(ky),
    ecg: ecgApi(ky),
    round: roundApi(ky),
    alert: alertApi(ky)
  }
}

const Plugin: PluginObject<undefined> = {
  install: Vue => {
    const api = _api()

    Vue.$api = api
    Object.defineProperty(Vue.prototype, '$api', {
      get() {
        return api
      }
    })
  }
}

export default Plugin
